import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, ArrowRight, School as SchoolIcon, Package, MapPin, Loader2, Coins, Boxes } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getAdministrationProfile, getSupplySchoolDetails } from '../api';

const numFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const moneyFmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));

export default function AdministrationDetailPage() {
  const { name } = useParams();
  const decoded = decodeURIComponent(name || '');
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);

  useEffect(() => {
    setLoading(true);
    getAdministrationProfile(decoded).then(setData).finally(() => setLoading(false));
  }, [decoded]);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="administration-profile">
        <button onClick={() => nav('/administrations')} className="text-[11.5px] text-slate-400 mb-3 hover:text-slate-200 flex items-center gap-1" data-testid="admin-back">
          <ArrowRight size={13} /> رجوع إلى الإدارات
        </button>
        <div className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'rgba(109,93,251,0.25)' }}>
            <Building2 size={22} style={{ color: '#9D8CFF' }} />
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>EDUCATION ADMINISTRATION · 360°</div>
            <h1 className="se-title mt-1">{decoded}</h1>
            <div className="se-subtitle">ملف كامل للإدارة التعليمية</div>
          </div>
        </div>

        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : !data?.found ? (
          <div className="se-panel p-12 text-center text-slate-400">لا توجد بيانات لهذه الإدارة</div>
        ) : (
          <>
            <section className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              <Kpi label="المدارس" value={numFmt(data.kpis.schools)} icon={SchoolIcon} tint="#9D8CFF" testid="admin-kpi-schools" />
              <Kpi label="الموردون" value={numFmt(data.kpis.suppliers)} icon={Building2} tint="#38D6C7" testid="admin-kpi-suppliers" />
              <Kpi label="الأصناف" value={numFmt(data.kpis.items)} icon={Boxes} tint="#F59E0B" testid="admin-kpi-items" />
              <Kpi label="المدن" value={numFmt(data.kpis.cities)} icon={MapPin} tint="#38D6C7" testid="admin-kpi-cities" />
              <Kpi label="الكمية" value={numFmt(data.kpis.allocated_qty)} icon={Package} tint="#9D8CFF" testid="admin-kpi-qty" />
              <Kpi label="القيمة (ر.س)" value={moneyFmt(data.kpis.allocated_value)} icon={Coins} tint="#F59E0B" testid="admin-kpi-value" />
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
              <div className="se-panel p-4" data-testid="admin-by-supplier">
                <div className="se-section-head mb-3">الموردون الأكبر</div>
                <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                  {(data.by_supplier || []).slice(0, 10).map((s) => (
                    <div key={s.name} className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-white/5">
                      <div className="truncate font-semibold" style={{ color: '#F1EEFB' }}>{s.name}</div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-slate-400">{numFmt(s.schools)} مدرسة</span>
                        <span className="tabular-nums font-bold" style={{ color: '#38D6C7' }}>{moneyFmt(s.allocated_value)} ر.س</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="se-panel p-4" data-testid="admin-by-item">
                <div className="se-section-head mb-3">أعلى الأصناف طلباً</div>
                <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                  {(data.by_item || []).map((it) => (
                    <div key={it.name} className="flex items-center justify-between text-[12.5px] py-1.5 border-b border-white/5">
                      <div className="truncate">{it.name}</div>
                      <div className="tabular-nums font-bold shrink-0" style={{ color: '#9D8CFF' }}>{numFmt(it.qty)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="se-panel p-0 mb-6" data-testid="admin-schools-table">
              <div className="se-section-head p-4 pb-3">مدارس هذه الإدارة ({numFmt(data.schools?.length || 0)})</div>
              <div className="overflow-x-auto">
                <table className="se-table">
                  <thead><tr>
                    <th>المدرسة</th><th>الرقم الوزاري</th><th>المدينة</th><th>المرحلة</th><th>المورد</th>
                    <th className="text-center">الأصناف</th><th className="text-center">الكمية</th><th className="text-center">القيمة</th>
                  </tr></thead>
                  <tbody>
                    {(data.schools || []).slice(0, 300).map((s, i) => (
                      <tr key={i} className="se-row-click" onClick={() => setSelectedSchool(s.school_internal_id || s.school_id || s.school_name)} data-testid={`admin-school-row-${i}`}>
                        <td className="font-semibold" style={{ color: '#F1EEFB' }}>{s.school_name || '—'}</td>
                        <td className="tabular-nums text-slate-400">{s.school_id || (s.school_id_missing ? <span style={{ color: '#F59E0B' }}>لا يوجد</span> : '—')}</td>
                        <td className="text-slate-400">{s.region || '—'}</td>
                        <td className="text-slate-400">{s.stage || '—'}</td>
                        <td className="text-slate-400">{s.supplier || '—'}</td>
                        <td className="text-center tabular-nums">{numFmt(s.items_count)}</td>
                        <td className="text-center tabular-nums font-bold" style={{ color: '#9D8CFF' }}>{numFmt(s.allocated_qty)}</td>
                        <td className="text-center tabular-nums">{moneyFmt(s.allocated_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.schools.length > 300 && (
                  <div className="text-center text-slate-500 text-[11px] py-2">عرض 300 من {numFmt(data.schools.length)} مدرسة</div>
                )}
              </div>
            </section>
          </>
        )}

        {selectedSchool && <SchoolModal schoolKey={selectedSchool} onClose={() => setSelectedSchool(null)} />}
      </main>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="se-kpi" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="se-kpi-label">{label}</div>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${tint}22`, border: `1px solid ${tint}44` }}>
          <Icon size={13} style={{ color: tint }} />
        </div>
      </div>
      <div className="se-kpi-value">{value}</div>
    </div>
  );
}

function SchoolModal({ schoolKey, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { getSupplySchoolDetails(schoolKey).then((d) => { setData(d); setLoading(false); }); }, [schoolKey]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,9,16,0.75)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div className="se-panel-hi max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
        {loading ? (
          <div className="p-10 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : !data?.found ? (
          <div className="p-10 text-center"><div className="font-bold" style={{ color: '#F1EEFB' }}>لا توجد بيانات توريد لهذه المدرسة</div><button className="se-btn se-btn-primary mt-4" onClick={onClose}>إغلاق</button></div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[11px] font-semibold tracking-wider" style={{ color: '#B7ADFF' }}>SCHOOL SUPPLY PROFILE</div>
                <div className="text-[20px] font-bold mt-1" style={{ color: '#F1EEFB' }}>{data.school_name || 'غير متوفر'}</div>
                <div className="text-slate-400 text-[12px] mt-1">
                  {data.school_id || (data.school_id_missing ? <span style={{ color: '#F59E0B' }}>لا يوجد رقم وزاري</span> : 'غير متوفر')} · {data.region || '—'}
                </div>
              </div>
              <button onClick={onClose} className="se-btn se-btn-ghost">إغلاق</button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <MiniStat label="الأصناف" value={numFmt(data.summary.items_count)} />
              <MiniStat label="الكمية" value={numFmt(data.summary.total_quantity)} tint="#9D8CFF" />
              <MiniStat label="القيمة" value={moneyFmt(data.summary.total_value)} tint="#38D6C7" />
            </div>
            <div className="se-panel overflow-x-auto" style={{ padding: 0 }}>
              <table className="se-table">
                <thead><tr><th>الصنف</th><th>المورد</th><th className="text-center">الكمية</th><th className="text-center">سعر الوحدة</th><th className="text-center">القيمة</th></tr></thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i}>
                      <td>{it.item || '—'}</td>
                      <td className="text-slate-400">{it.company || '—'}</td>
                      <td className="text-center tabular-nums">{numFmt(it.quantity)}</td>
                      <td className="text-center tabular-nums">{numFmt(it.unit_price)}</td>
                      <td className="text-center tabular-nums">{moneyFmt(it.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint = '#F1EEFB' }) {
  return (
    <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="text-[16px] font-bold tabular-nums mt-0.5" style={{ color: tint }}>{value}</div>
    </div>
  );
}
